import { useState, useEffect } from 'react';
import { mormSigner, WasmStatus, MormSignerStatus } from '../sdk';

/**
 * Custom hook for managing MormSigner WASM initialization and status
 * 
 * @returns Object containing:
 *   - wasmStatus: Current WASM loading status
 *   - isReady: Boolean indicating if WASM is ready to use
 *   - status: Detailed MormSigner status information
 *   - retry: Function to retry WASM initialization
 */
export function useMormSigner() {
  const [wasmStatus, setWasmStatus] = useState<WasmStatus>({
    loading: true,
    loaded: false,
    error: null
  });

  const [status, setStatus] = useState<MormSignerStatus>({
    initialized: false,
    wasmLoaded: false,
    ready: false
  });

  // Initialize WASM on mount
  useEffect(() => {
    const initializeWasm = async (): Promise<void> => {
      try {
        setWasmStatus(prev => ({ ...prev, loading: true, error: null }));
        const success = await mormSigner.initialize();
        
        if (success) {
          setWasmStatus({ loading: false, loaded: true, error: null });
          setStatus(mormSigner.getStatus());
        } else {
          setWasmStatus({ 
            loading: false, 
            loaded: false, 
            error: 'Failed to load WASM module' 
          });
          setStatus(mormSigner.getStatus());
        }
      } catch (error) {
        setWasmStatus({ 
          loading: false, 
          loaded: false, 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
        setStatus(mormSigner.getStatus());
      }
    };

    initializeWasm();
  }, []);

  // Update status periodically to reflect changes
  useEffect(() => {
    if (wasmStatus.loaded) {
      const interval = setInterval(() => {
        setStatus(mormSigner.getStatus());
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [wasmStatus.loaded]);

  // Retry initialization
  const retry = async (): Promise<void> => {
    setWasmStatus({ loading: true, loaded: false, error: null });
    try {
      const success = await mormSigner.initialize();
      
      if (success) {
        setWasmStatus({ loading: false, loaded: true, error: null });
        setStatus(mormSigner.getStatus());
      } else {
        setWasmStatus({ 
          loading: false, 
          loaded: false, 
          error: 'Failed to load WASM module' 
        });
        setStatus(mormSigner.getStatus());
      }
    } catch (error) {
      setWasmStatus({ 
        loading: false, 
        loaded: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setStatus(mormSigner.getStatus());
    }
  };

  return {
    wasmStatus,
    isReady: wasmStatus.loaded && status.ready,
    status,
    retry
  };
}
